import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { notificationService } from "@/services";
import { useNotifications } from "@/context/NotificationContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";

const PRIORITY_DOT = {
  critical: "bg-red-500", high: "bg-amber-500", normal: "bg-primary", low: "bg-slate-400",
};

export default function NotificationsPage() {
  const { load: refreshBadge } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await notificationService.list({ size: 100 });
      setItems(d.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const markRead = async (id) => { await notificationService.markRead(id); refresh(); refreshBadge(); };
  const markAll = async () => {
    await notificationService.markAll();
    toast.success("All marked read");
    refresh(); refreshBadge();
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle="Your in-app notification center"
        actions={
          <button
            onClick={markAll}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors"
            data-testid="notifications-mark-all"
          >
            <CheckCheck className="h-4 w-4" /> Mark all read
          </button>
        }
      />
      <div className="bg-card rounded-xl border border-border shadow-sm divide-y divide-border" data-testid="notifications-list">
        {loading ? (
          <div className="px-4 py-12 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-16 text-center text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-3 opacity-40" />
            No notifications
          </div>
        ) : (
          items.map((n) => (
            <div
              key={n.id}
              className={`flex items-start gap-3 px-4 py-3.5 ${n.read ? "" : "bg-muted/40"}`}
              data-testid={`notification-row-${n.id}`}
            >
              <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[n.priority] || PRIORITY_DOT.normal}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{n.title}</p>
                <p className="text-sm text-muted-foreground">{n.message}</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {(n.createdOn || "").slice(0, 16).replace("T", " ")} · {n.channel}
                </p>
              </div>
              {!n.read && (
                <button
                  onClick={() => markRead(n.id)}
                  className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
                  title="Mark read"
                  data-testid={`notification-mark-${n.id}`}
                >
                  <Check className="h-4 w-4" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
